import { Card, Tooltip } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';
import dayjs, { Dayjs } from 'dayjs';
import { useEffect, useMemo } from 'react';
import { useOutletContext } from 'react-router';
import CustomDataGrid from '../../../components/CustomDataGrid';
import { useAppDataContext } from '../../../contexts/appData';
import { useLanguage } from '../../../contexts/language';
import {
  DAYJS_VISUALIZATION_DATE_FORMAT,
  DAYJS_VISUALIZATION_TIME_FORMAT,
} from '../../../utils/date';
import {
  AdHocBadge,
  JourneyEntityStatusBadge,
  JourneyStatusBadge,
} from '../Journeys/JourneyStatusBadges';
import { InvoiceTemplateSubPageContext } from './types';
import { JourneyStatus, EntityStatus } from '../../../types/core';

const SEPARATOR = ';;';

interface DisplayedInvoiceJourney {
  id: string;
  day: string;
  time: string;
  datetime: Dayjs;
  departurePort: string;
  arrivalPort: string;
  isAdhoc: boolean;
  journeyStatus: JourneyStatus;
  entityStatus: EntityStatus;
}

const InvoiceJourneysPage = () => {
  const { t, language } = useLanguage();
  const { portsMap } = useAppDataContext();

  const {
    journeysManagement: { journeys, isFetching, refetch },
  } = useOutletContext<InvoiceTemplateSubPageContext>();

  const displayedInvoiceJourneys = useMemo(() => {
    return journeys.map<DisplayedInvoiceJourney>(journey => {
      const departurePortItem = portsMap[journey.departurePortId];
      const arrivalPortItem = portsMap[journey.arrivalPortId];
      return {
        id: journey.id,
        time: journey.time,
        day: journey.day,
        datetime: dayjs(`${journey.day}T${journey.time}`),
        departurePort: [
          departurePortItem.portInstance.islandNames[language],
          departurePortItem.label,
        ].join(SEPARATOR),
        arrivalPort: [
          arrivalPortItem.portInstance.islandNames[language],
          arrivalPortItem.label,
        ].join(SEPARATOR),
        isAdhoc: journey.isAdhoc,
        journeyStatus: journey.journeyStatus,
        entityStatus: journey.entityStatus,
      };
    });
  }, [journeys, language, portsMap]);

  useEffect(() => {
    if (!isFetching) {
      refetch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const columns = useMemo<GridColDef<DisplayedInvoiceJourney>[]>(
    () => [
      {
        field: 'day',
        flex: 1,
        minWidth: 140,
        headerName: t('content.date'),
        renderCell: params =>
          params.row.datetime.format(DAYJS_VISUALIZATION_DATE_FORMAT),
      },
      {
        field: 'time',
        flex: 1,
        minWidth: 100,
        headerName: t('content.time'),
        renderCell: params =>
          params.row.datetime.format(DAYJS_VISUALIZATION_TIME_FORMAT),
      },
      {
        field: 'departurePort',
        flex: 3,
        minWidth: 160,
        headerName: t('content.departure-port'),
        renderCell: params => {
          const [islandName, portName] =
            params.row.departurePort.split(SEPARATOR);
          return (
            <Tooltip title={portName}>
              <span>{islandName}</span>
            </Tooltip>
          );
        },
      },
      {
        field: 'arrivalPort',
        flex: 3,
        headerName: t('content.arrival-port'),
        minWidth: 160,
        renderCell: params => {
          const [islandName, portName] =
            params.row.arrivalPort.split(SEPARATOR);
          return (
            <Tooltip title={portName}>
              <span>{islandName}</span>
            </Tooltip>
          );
        },
      },
      {
        field: 'isAdhoc',
        flex: 1,
        minWidth: 160,
        headerName: '',
        type: 'boolean',
        renderCell: ({ row }) => (row.isAdhoc ? <AdHocBadge small /> : null),
      },
      {
        field: 'journeyStatus',
        flex: 1,
        minWidth: 160,
        headerName: t('content.journey-status'),
        type: 'singleSelect',
        valueOptions: Object.values(JourneyStatus).map(status => ({
          value: status,
          label: t(`journey-status.${status}`),
        })),
        renderCell: params => (
          <JourneyStatusBadge
            journeyStatus={params.row.journeyStatus}
            journeyDay={params.row.day}
            journeyTime={params.row.time}
            small
          />
        ),
      },
      {
        field: 'entityStatus',
        flex: 1,
        minWidth: 180,
        headerName: t('content.log-status'),
        type: 'singleSelect',
        valueOptions: Object.values(EntityStatus).map(status => ({
          value: status,
          label: t(`journey-log-status.${status}`),
        })),
        renderCell: params =>
          params.row.id ? (
            <JourneyEntityStatusBadge
              entityStatus={params.row.entityStatus}
              small
            />
          ) : null,
      },
    ],
    [t],
  );

  return (
    <div className="relative h-[calc(100svh-160px)] md:h-[calc(100svh-208px)]">
      <Card className="absolute inset-0 overflow-auto">
        <CustomDataGrid
          rows={displayedInvoiceJourneys}
          loading={isFetching}
          columns={columns}
          initialState={{
            sorting: {
              sortModel: [
                {
                  field: 'day',
                  sort: 'asc',
                },
              ],
            },
          }}
        />
      </Card>
    </div>
  );
};

export default InvoiceJourneysPage;
